import { UserPreview } from '@main/access-control-types';
import { mkInput, mkType, type SchemaToType } from '@main/schema-utils';

export const ConsentHost = mkType({
  name: 'ConsentHost',
  comment: 'Hosts found through telemetry or added by users',
  fields: {
    id: {
      type: 'id',
      comment: 'The ID of this site record',
      modelName: 'consentSite',
    },
    host: {
      type: 'string',
      comment: 'The host for the site',
      optional: true,
    },
    user: {
      type: UserPreview,
      comment:
        'Indicates how the row was added to the table; user ID or null (will display "Transcend Consent")',
      optional: true,
    },
    lastDiscoveredAt: {
      type: 'Date',
      comment:
        'Indicates when telemetry data has been encountered from this site, and if so how old it is',
      optional: true,
    },
    description: {
      type: 'string',
      comment: 'Optional free text notes field',
      optional: true,
    },
    createdAt: {
      type: 'Date',
      comment:
        'The date the site was first added to the table, whether by telemetry or manually',
    },
    deletedAt: {
      type: 'Date',
      comment:
        'The date the site was deleted by the user, or reverted by telemetry',
      optional: true,
    },
  },
});

/** Override type */
export type ConsentHost = SchemaToType<typeof ConsentHost>;

export const ConsentSite = mkType({
  name: 'ConsentSite',
  comment: 'Sites found through telemetry or added by users',
  fields: {
    lastDiscoveredAt: {
      type: 'Date',
      comment:
        'Indicates when telemetry data was most recently encountered from this site, if it was encountered',
      optional: true,
    },
    site: {
      type: 'string',
      comment: 'The eTLD+1 of the URL telemetry was emitted from',
    },
    createdAt: {
      type: 'Date',
      comment:
        'The time when telemetry from the site was first encountered (or when the site was added manually)',
    },
    hosts: {
      type: ConsentHost,
      comment: 'List of encountered hosts that are descendant to the site',
      list: true,
    },
  },
});

/** Override type */
export type ConsentSite = SchemaToType<typeof ConsentSite>;

export const FilterConsentSiteInput = mkInput({
  name: 'FilterConsentSiteInput',
  comment: 'Input for filtering consent sites',
  fields: {
    lastDiscoveredAtBefore: {
      type: 'Date',
      comment: 'Filter for earliest date telemetry data was encountered',
      optional: true,
    },
    lastDiscoveredAtAfter: {
      type: 'Date',
      comment: 'Filter for most recent date telemetry data was encountered',
      optional: true,
    },
    host: {
      type: 'string',
      comment: 'Filters based on either the host or site string',
      optional: true,
    },
    userId: {
      type: 'string',
      comment:
        'Indicates how the row was added to the table; user ID or if by Telemetry data, show "Transcend Consent"',
      optional: true,
      list: true,
    },
    description: {
      type: 'string',
      comment: 'Optional free text notes field',
      optional: true,
    },
    includeNulls: {
      type: 'string',
      list: true,
      comment: 'Fields to filter on null values',
      optional: true,
    },
  },
});

/** Override type */
export type FilterConsentSiteInput = SchemaToType<
  typeof FilterConsentSiteInput
>;

export const CreateOrUpdateConsentSiteInput = mkInput({
  name: 'CreateOrUpdateConsentSiteInput',
  comment: 'Data used to create or update a consent site record',
  fields: {
    id: {
      type: 'id',
      comment: 'The ID of this site',
      modelName: 'consentSite',
      optional: true,
    },
    site: {
      type: 'string',
      comment: 'Filters based on the site',
      optional: true,
    },
    host: {
      type: 'string',
      comment: 'Filters based on the host',
      optional: true,
    },
    userId: {
      type: 'string',
      comment:
        'Indicates how the row was added to the table; user ID or if by Telemetry data, show "Transcend Consent"',
      optional: true,
    },
    description: {
      type: 'string',
      comment: 'Optional free text notes field',
      optional: true,
    },
    airgapBundleId: {
      type: 'id',
      modelName: 'airgapBundle',
      comment: 'ID of the airgap bundle that this consent site belongs to',
    },
  },
});

/** Override type */
export type CreateOrUpdateConsentSiteInput = SchemaToType<
  typeof CreateOrUpdateConsentSiteInput
>;

export const DeleteConsentSiteInput = mkInput({
  name: 'DeleteConsentSiteInput',
  comment: 'Input for deleting consent sites by ID',
  fields: {
    id: {
      type: 'id',
      modelName: 'consentSite',
      comment: 'ID of the consent site record to delete',
    },
  },
});

/** Override type */
export type DeleteConsentSiteInput = SchemaToType<
  typeof DeleteConsentSiteInput
>;
